import { useState } from 'react';
import { useIntl } from 'react-intl';

import { toastCaller } from '@/shared/ui';

import { templatesModel, type DocumentTemplate } from '@/entities/templates';

import {
  getSaveRefusalIssues,
  getSaveRefusalText,
  splitSaveRefusal,
} from '../lib';

import { useApplyTemplateVersion } from './useApplyTemplateVersion';

// "Save as my template": a template changed before the start, a block note's
// or a summary's or an evaluation's fields, saved as the clinic's next
// version of it, through the same save as the Templates page, based on the
// version it was opened on. Resolves with the saved version, or null once
// the refusal is shown.
export const useSavePresetTemplate = () => {
  const { formatMessage } = useIntl();
  const { save: saveVersion } = useApplyTemplateVersion();
  const [saving, setSaving] = useState(false);

  const describeRefusal = (
    error: unknown,
  ): { heading?: string; message: string } => {
    if (templatesModel.isSeriesBusyWriteError(error)) {
      return {
        message: formatMessage({
          id: 'workspace.templateEdit.saveBusy',
          defaultMessage:
            'Another save of this template is still under way. Try again in a moment.',
        }),
      };
    }
    if (templatesModel.isSeriesMovedWriteError(error)) {
      return {
        message: formatMessage({
          id: 'workspace.start.templateMoved',
          defaultMessage:
            'The template was changed elsewhere after this note was opened, so it was not saved. Your values and changes are kept for this recording.',
        }),
      };
    }
    const failed = formatMessage({
      id: 'workspace.template.versionFailed',
      defaultMessage:
        'The new template version was not saved. Your changes are kept; try again.',
    });
    const issues = getSaveRefusalIssues(error);
    const refusal = getSaveRefusalText(error);
    const problems = issues.length
      ? issues.map((issue) => issue.Message || issue.Kind)
      : refusal
        ? splitSaveRefusal(refusal)
        : [];
    return problems.length
      ? { heading: failed, message: problems.join('; ') }
      : { message: failed };
  };

  const save = async ({
    source,
    template: changed,
  }: {
    source: DocumentTemplate;
    template: DocumentTemplate;
  }): Promise<DocumentTemplate | null> => {
    setSaving(true);
    try {
      const { template } = await saveVersion({
        source,
        draft: { template: changed, answers: {}, aiAnswers: {} },
      });
      toastCaller({
        type: 'success',
        message: formatMessage({
          id: 'workspace.template.versionSaved',
          defaultMessage:
            'Saved as a new version of the clinic template. Encounters started from now on use it.',
        }),
      });
      return template;
    } catch (error) {
      toastCaller({
        type: 'error',
        ...describeRefusal(error),
        autoClose: 10000,
      });
      return null;
    } finally {
      setSaving(false);
    }
  };

  return { save, saving };
};
